import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Briefcase, ChatCircle, Star, CrownSimple, Pencil, ShieldCheck } from "@phosphor-icons/react";

export default function WorkerDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [convs, setConvs] = useState([]);

  useEffect(() => {
    api.get("/workers/me").then((r) => setProfile(r.data));
    api.get("/jobs").then((r) => setJobs(r.data));
    api.get("/conversations").then((r) => setConvs(r.data));
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Pro Dashboard</div>
            <h1 className="font-display text-4xl">Welcome, {user?.name?.split(" ")[0]}.</h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/worker/onboarding" className="inline-flex items-center gap-2 bg-white border border-slate-300 px-4 py-2.5 font-semibold text-sm hover:bg-slate-50" data-testid="edit-profile-btn">
              <Pencil size={16} /> {profile ? "Edit profile" : "Create profile"}
            </Link>
            <Link to="/worker/verification" className="inline-flex items-center gap-2 bg-white border border-slate-300 px-4 py-2.5 font-semibold text-sm hover:bg-slate-50" data-testid="verify-btn">
              <ShieldCheck size={16} className="text-emerald-600" /> {profile?.verification?.status === "verified" ? "Verified" : profile?.verification?.status === "pending" ? "Pending review" : "Get verified"}
            </Link>
            <Link to="/pricing" className="inline-flex items-center gap-2 bg-accent-red text-white px-5 py-3 font-semibold hover:bg-[#D55915]" data-testid="upgrade-btn">
              <CrownSimple size={18} weight="fill" /> {profile?.subscription_active ? "Manage Plan" : "Upgrade"}
            </Link>
          </div>
        </div>

        {!profile && (
          <div className="border-2 border-accent-red bg-red-50 p-6 mb-8">
            <div className="font-display text-xl mb-1">Complete your profile</div>
            <p className="text-sm text-slate-700 mb-3">You need a profile to receive leads.</p>
            <Link to="/worker/onboarding" className="bg-accent-red text-white px-4 py-2 text-sm font-semibold" data-testid="complete-profile-btn">Get started →</Link>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-10">
          <div className="border border-slate-200 p-6">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Rating</div>
            <div className="font-display text-4xl mt-2 flex items-center gap-2">
              {profile?.rating?.toFixed?.(1) || "—"}
              <Star size={20} weight="fill" className="text-amber-500" />
            </div>
            <div className="text-xs text-slate-500">{profile?.review_count || 0} reviews</div>
          </div>
          <div className="border border-slate-200 p-6">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Available leads</div>
            <div className="font-display text-4xl mt-2">{jobs.length}</div>
          </div>
          <div className="border border-slate-200 p-6">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Conversations</div>
            <div className="font-display text-4xl mt-2">{convs.length}</div>
          </div>
          <div className={`border p-6 ${profile?.subscription_active ? "bg-brand text-white border-brand" : "border-slate-200"}`}>
            <div className={`text-xs uppercase tracking-[0.2em] font-semibold ${profile?.subscription_active ? "opacity-80" : "text-slate-500"}`}>Plan</div>
            <div className="font-display text-3xl mt-2">{profile?.subscription_active ? "Pro" : "Free"}</div>
            {!profile?.subscription_active && <Link to="/pricing" className="text-xs font-semibold text-brand">Upgrade →</Link>}
          </div>
        </div>

        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl">Open jobs in your area</h2>
            <Link to="/jobs" className="text-sm font-semibold text-brand">All jobs →</Link>
          </div>
          {jobs.length === 0 ? (
            <div className="border border-dashed border-slate-300 p-8 text-center">
              <Briefcase size={28} className="mx-auto text-slate-400 mb-2" />
              <p className="text-sm text-slate-600">No open jobs right now. Check back soon.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {jobs.slice(0, 6).map((j) => (
                <Link key={j.job_id} to={`/jobs/${j.job_id}`} className="block border border-slate-200 p-5 hover:-translate-y-1 hover:shadow-md transition-all">
                  <div className="font-display text-lg">{j.title}</div>
                  <div className="text-xs text-slate-500 mt-1">{j.category} · {j.city}, {j.province}</div>
                  <p className="text-sm text-slate-700 mt-2 line-clamp-2">{j.description}</p>
                  <div className="text-sm font-semibold mt-3">${j.budget_min} – ${j.budget_max} CAD</div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
      <Footer />
    </div>
  );
}
